import { compact, omit } from 'lodash';
import dayjs, { Dayjs, OpUnitType } from 'dayjs';
import toObject from 'dayjs/plugin/toObject';
import { presetPalettes as Palette } from '@ant-design/colors';

dayjs.extend(toObject);

const Y_AXIS_MAP: Record<string, number> = {
  clicks: 1,
  impressions: 2,
  installs: 3,
};

const DEFAULT_BAR_FIELDS = ['postbacks', 'valid_postbacks', 'partner_valid_postbacks'];

const selectedColors = omit(Palette, ['volcano', 'gold', 'green', 'purple', 'grey']);
const COLORS = {
  Bar: Object.values(selectedColors).map((color) => color[5]),
  Line: Object.values(selectedColors).map((color) => color[4]),
  Pie: [
    Palette.purple[4],
    Palette.lime[4],
    Palette.gold[4],
    Palette.red[4],
    Palette.cyan[4],
    Palette.yellow[4],
  ],
  Empty: '#f0f2f5',
};

function mapSumsToAxis(sums: string[]) {
  return Object.fromEntries(sums.map((sum) => [sum, Y_AXIS_MAP[sum] || 0]));
}

function isDateGroup(group: string) {
  return group.match(/(day|month|year|hour)/);
}

type DateType = 'day' | 'month' | 'year' | 'hour';

function compareDateItem(field: DateType, data: number, dateObj: ReturnType<Dayjs['toObject']>) {
  if (field === 'day') return data === dateObj.date;
  if (field === 'month') return data === dateObj.months + 1;
  if (field === 'year') return data === dateObj.years;
  if (field === 'hour') return data === dateObj.hours;
  return false;
}

function getDateFormat(groupArr: string[]) {
  if (groupArr[0] === 'hour') return 'HH';
  const format = [];
  if (groupArr.includes('year')) format[0] = 'YYYY';
  if (groupArr.includes('month')) {
    format[1] = 'MM';
    if (!groupArr.includes('day')) format[1] = 'MMM';
  }
  if (groupArr.includes('day')) format[2] = 'DD';
  return compact(format).join('-');
}

function normalizeDataByDate(data: any, sums: string[], group: string, [from, to]: [Date, Date]) {
  //@ts-ignore
  const groupSplit: DateType[] = group.split(':');
  //@ts-ignore
  const incrementer: OpUnitType = groupSplit[groupSplit.length - 1];
  let fromDate = dayjs(from);
  let endDate = dayjs(to);

  if (group === 'hour') {
    fromDate = fromDate.startOf('day');
    endDate = fromDate.hour(23);
  }

  let date = fromDate;
  const resultData = [];

  while (date.isSame(endDate, 'hour') || date.isBefore(endDate, 'hour')) {
    const dateObj = date.toObject();
    let existing = data?.find((item: any) =>
      groupSplit.every((groupKey: DateType) => compareDateItem(groupKey, item[groupKey], dateObj)),
    );
    if (!existing) {
      existing = {};
      sums.forEach((sum) => {
        existing[sum] = 0;
      });
    }
    delete existing._id;
    resultData.push({ ...existing, dateString: date.format(getDateFormat(groupSplit)) });
    date = date.add(1, incrementer);
  }

  // Remove duplicate dates when group by day
  if (group === 'day' && !fromDate.isSame(endDate, 'month')) {
    const duplicateDates = endDate.date() - fromDate.date() + 1;
    if (duplicateDates > 0) return resultData.slice(duplicateDates);
  }

  return resultData;
}

function normalizeNameFields(data: any, group: string) {
  const nameField = `${group}Name`;
  return data.map((d: any) => {
    const result = omit(d, '_id');
    if (d[nameField]) {
      const id = d[group];
      result[group] = `${d[nameField]}${id && id !== '-' ? ` (${id})` : ''}`;
      delete result[nameField];
    }
    else {
      result[group] = result[group] ? (result[group].name ? String(result[group].name) : String(result[group])) : 'No key';
    }
    return result;
  });
}

export {
  DEFAULT_BAR_FIELDS,
  COLORS,
  mapSumsToAxis,
  isDateGroup,
  normalizeDataByDate,
  normalizeNameFields,
};
